import { Button } from "@/components/ui/button";
import { X, RotateCcw, Download, ZoomIn, ZoomOut, Share2, Edit3, Trash2 } from "lucide-react";
import { useState, useCallback, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface EnhancedPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  photo: {
    id: string;
    title: string;
    image: string;
    description?: string;
    timestamp?: number;
  };
  onRetake?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  showEditTools?: boolean;
}

export function EnhancedPhotoModal({
  isOpen,
  onClose,
  photo,
  onRetake,
  onDownload,
  onDelete,
  onShare,
  showEditTools = false
}: EnhancedPhotoModalProps) {
  const isMobile = useIsMobile();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [showFilters, setShowFilters] = useState(false);
  
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(() => {
    if (!onDownload) {
      // Default download behavior
      const link = document.createElement('a');
      link.href = photo.image;
      link.download = `${photo.title.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      onDownload();
    }
  }, [photo, onDownload]);

  const handleShare = useCallback(async () => {
    if (onShare) {
      onShare();
      return;
    }

    // Native Web Share API
    if (navigator.share) {
      try {
        // Convert data URL to blob
        const response = await fetch(photo.image);
        const blob = await response.blob();
        const file = new File([blob], `${photo.title}.jpg`, { type: 'image/jpeg' });
        
        await navigator.share({
          title: photo.title,
          text: `Check out this dental photo: ${photo.title}`,
          files: [file]
        });
      } catch (error) {
        console.error('Share failed:', error);
        // Fallback to copying image URL
        navigator.clipboard.writeText(photo.image);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(photo.image);
        alert('Photo URL copied to clipboard!');
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  }, [photo, onShare]);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel / 1.5, 1);
    setZoomLevel(newZoom);
    if (newZoom === 1) {
      setPanPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  }, [zoomLevel, panPosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoomLevel]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const resetFilters = useCallback(() => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
  }, []);

  const formatTimestamp = useCallback((timestamp?: number) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString();
  }, []);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-2 sm:p-4" 
      onClick={onClose}
    >
      <div 
        className={cn(
          "bg-white rounded-lg w-full max-h-[95vh] overflow-hidden",
          isMobile ? "max-w-sm" : "max-w-6xl",
          "flex flex-col"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate">{photo.title}</h3>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {photo.description && (
                <span className="truncate">{photo.description}</span>
              )}
              {photo.timestamp && (
                <span className="text-xs">{formatTimestamp(photo.timestamp)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showEditTools && (
              <Button 
                onClick={() => setShowFilters(!showFilters)} 
                variant="ghost" 
                size="sm"
                className={cn(showFilters && "bg-gray-100")}
              >
                <Edit3 className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="p-4 border-b bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Brightness</label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={brightness}
                  onChange={(e) => setBrightness(Number(e.target.value))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{brightness}%</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contrast</label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={contrast}
                  onChange={(e) => setContrast(Number(e.target.value))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{contrast}%</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Saturation</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={saturation}
                  onChange={(e) => setSaturation(Number(e.target.value))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{saturation}%</span>
              </div>
            </div>
            <div className="flex justify-end mt-2">
              <Button onClick={resetFilters} variant="outline" size="sm">
                Reset Filters
              </Button>
            </div>
          </div>
        )}

        {/* Image Container */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-hidden bg-gray-50 relative cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div 
            className="w-full h-full flex items-center justify-center p-4"
            style={{
              transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}
          >
            <img 
              ref={imageRef}
              src={photo.image} 
              alt={photo.title}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              style={{
                maxHeight: isMobile ? '70vh' : '70vh',
                maxWidth: isMobile ? '90vw' : '100%',
                filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`
              }}
              draggable={false}
            />
          </div>

          {/* Zoom Controls */}
          <div className="absolute top-4 right-4 flex flex-col gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomIn}
              className="bg-white/90 hover:bg-white"
              disabled={zoomLevel >= 5}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomOut}
              className="bg-white/90 hover:bg-white"
              disabled={zoomLevel <= 1}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            {zoomLevel > 1 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={resetZoom}
                className="bg-white/90 hover:bg-white text-xs px-2"
              >
                Reset
              </Button>
            )}
          </div>

          {/* Zoom Level Indicator */}
          {zoomLevel > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-sm">
              {Math.round(zoomLevel * 100)}%
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-white">
          <div className={cn(
            "flex gap-2",
            isMobile ? "flex-col" : "flex-row flex-wrap"
          )}>
            {onRetake && (
              <Button 
                onClick={onRetake}
                variant="outline"
                className={cn(
                  "flex items-center justify-center gap-2",
                  isMobile ? "w-full" : "flex-1"
                )}
              >
                <RotateCcw className="w-4 h-4" />
                Retake Photo
              </Button>
            )}
            
            <Button 
              onClick={handleDownload}
              variant="outline"
              className={cn(
                "flex items-center justify-center gap-2",
                isMobile ? "w-full" : "flex-1"
              )}
            >
              <Download className="w-4 h-4" />
              Download
            </Button>

            <Button 
              onClick={handleShare}
              variant="outline"
              className={cn(
                "flex items-center justify-center gap-2",
                isMobile ? "w-full" : "flex-1"
              )}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>

            {onDelete && (
              <Button 
                onClick={onDelete}
                variant="outline"
                className={cn(
                  "flex items-center justify-center gap-2 text-red-600 border-red-200 hover:bg-red-50",
                  isMobile ? "w-full" : "flex-1"
                )}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            )}
            
            <Button 
              onClick={onClose}
              className={cn(
                "flex items-center justify-center gap-2",
                isMobile ? "w-full" : "flex-1"
              )}
            >
              Keep Photo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}